import { DemoDataService } from './DemoDataService';

// Servicio para interceptar llamadas de API cuando estamos en modo demo
export class DemoApiService {
  
  // Verificar si estamos en modo demo
  static isDemoMode() {
    return sessionStorage.getItem('isDemoMode') === 'true';
  }

  // Interceptar llamadas de reportes
  static async interceptReportsAPI(originalCall, url, method, data) {
    if (!this.isDemoMode()) {
      return originalCall();
    }

    console.log('🎬 Demo Mode: Interceptando llamada API:', url, method);

    // Interceptar llamadas a reportes por planta cuando hay problemas de autorización
    if (url.includes('/reportes/planta/')) {
      try {
        // Intentar llamada original primero
        return await originalCall();
      } catch (error) {
        // Si falla por autorización (403) o planta inválida, usar datos mock
        if (error.response?.status === 403 || url.includes('/null') || url.includes('/undefined')) {
          console.log('🎬 Demo: Error de autorización o planta inválida, usando datos mock');
          return await this.getMockReportsByPlant();
        }
        throw error;
      }
    }
    
    // Interceptar SOLO las firmas de reportes
    if (url.includes('/reportes/') && url.includes('/firmar') && method.toLowerCase() === 'put') {
      return await this.mockSignReport(data);
    }

    // Para reportes filtrados
    if (url.includes('/reportes/filtrados')) {
      try {
        return await originalCall();
      } catch (error) {
        if (error.response?.status === 403) {
          console.log('🎬 Demo: Error de autorización en reportes filtrados, usando datos mock');
          return await this.getMockFilteredReports(data);
        }
        throw error;
      }
    }

    // Para reportes completados (estadísticas), usar mock
    if (url.includes('/reportes/completados')) {
      return await this.getMockCompletedReports(data);
    }

    // Si no se intercepta, ejecutar llamada original
    return originalCall();
  }

  // Obtener reportes mock por planta
  static async getMockReportsByPlant() {
    const demoData = await DemoDataService.getDemoData();
    
    if (!demoData?.reports) {
      throw new Error('No hay datos de demo disponibles');
    }

    console.log('📊 Devolviendo reportes mock de planta');
    return { data: demoData.reports };
  }

  // Obtener reportes filtrados mock
  static async getMockFilteredReports(filters) {
    const filteredReports = await DemoDataService.getDemoReportsFiltered(filters);
    
    console.log('🔍 Devolviendo reportes filtrados mock');
    return { data: filteredReports };
  }

  // Obtener reportes completados mock
  static async getMockCompletedReports(data) {
    const completedReports = await DemoDataService.getDemoReportsCompleted(
      data?.idPlanta, 
      data?.date
    );
    
    console.log('✅ Devolviendo reportes completados mock');
    return { data: completedReports };
  }

  // Simular firma de reporte (solo local)
  static async mockSignReport(data) {
    await DemoDataService.simulateApiDelay(500, 1000);
    
    console.log('✍️ Simulando firma de reporte localmente');
    
    // Almacenar firma localmente
    if (data && data.idReporte) {
      const signedReports = JSON.parse(localStorage.getItem('demoSignedReports') || '[]');
      if (!signedReports.includes(data.idReporte)) {
        signedReports.push(data.idReporte);
        localStorage.setItem('demoSignedReports', JSON.stringify(signedReports));
      }
    }
    
    return { 
      data: { 
        success: true, 
        message: 'Reporte firmado exitosamente (demo - solo local)' 
      } 
    };
  }

  // Interceptar llamadas de estadísticas
  static async interceptStatisticsAPI(originalCall, url, method, data) {
    if (!this.isDemoMode()) {
      return originalCall();
    }

    console.log('🎬 Demo Mode: Interceptando estadísticas:', url);
    await DemoDataService.simulateApiDelay(200, 500);

    const demoData = await DemoDataService.getDemoData();
    
    if (!demoData?.statistics) {
      return originalCall();
    }

    // Interceptar solo estadísticas específicas
    if (url.includes('/reportes/filtrados') && data && (data.tipo_mantenimiento === null || data.empresa)) {
      // Si es para estadísticas de gráfico, usar datos mock
      const weeklyReports = demoData.statistics.reportesPorSemana || [];
      return { data: weeklyReports };
    }

    if (url.includes('/reportes/completados')) {
      const completed = demoData.statistics.reportesCompletados || {};
      return { data: [completed] };
    }

    // Para otras llamadas, usar API real
    return originalCall();
  }

  // Interceptar llamadas de plantas
  static async interceptPlantAPI(originalCall, url, method, data) {
    if (!this.isDemoMode()) {
      return originalCall();
    }

    console.log('🎬 Demo Mode: Verificando plantas:', url);

    // Para planta ID 44, usar API real
    if (url.includes('/plantas/44') || url.includes('/plantas/imagen/')) {
      console.log('🏭 Usando API real para planta Darpha Fire Solutions');
      return originalCall();
    }

    // Para otras plantas, usar datos mock si es necesario
    const demoData = await DemoDataService.getDemoData();
    
    if (demoData?.plantInfo) {
      return { 
        data: demoData.plantInfo
      };
    }

    return originalCall();
  }

  // Interceptar cualquier llamada de API
  static async interceptAPI(originalCall, config) {
    if (!this.isDemoMode()) {
      return originalCall();
    }

    const { url, method, data } = config;

    // Reportes (firmas y estadísticas específicas)
    if (url?.includes('/reportes')) {
      return this.interceptReportsAPI(originalCall, url, method, data);
    }

    // Estadísticas específicas
    if (url?.includes('/reportes/filtrados') || url?.includes('/reportes/completados')) {
      return this.interceptStatisticsAPI(originalCall, url, method, data);
    }

    // Plantas (solo si no es ID 44)
    if (url?.includes('/plantas') && !url.includes('/plantas/44')) {
      return this.interceptPlantAPI(originalCall, url, method, data);
    }

    // Por defecto, ejecutar llamada original (API real)
    return originalCall();
  }

  // Método para habilitar la interceptación global
  static enableGlobalInterception() {
    if (typeof window !== 'undefined') {
      // Marcar que la interceptación está habilitada
      window.demoApiInterceptionEnabled = true;
      console.log('🎬 Demo API Interception habilitada');
    }
  }

  // Método para deshabilitar la interceptación global
  static disableGlobalInterception() {
    if (typeof window !== 'undefined') {
      window.demoApiInterceptionEnabled = false;
      console.log('🎬 Demo API Interception deshabilitada');
    }
  }

  // Verificar si un reporte está firmado localmente
  static isReportSignedLocally(idReporte) {
    if (!this.isDemoMode()) {
      return false;
    }

    const signedReports = JSON.parse(localStorage.getItem('demoSignedReports') || '[]');
    return signedReports.includes(idReporte);
  }

  // Limpiar firmas locales
  static clearLocalSignatures() {
    localStorage.removeItem('demoSignedReports');
    console.log('🗑️ Firmas locales limpiadas');
  }

  // Obtener lista de reportes firmados localmente
  static getLocallySignedReports() {
    return JSON.parse(localStorage.getItem('demoSignedReports') || '[]');
  }
}
